import { cn } from '@leoclinic/ui';
import { useSettings } from '@leoclinic/hooks';
import { useAppAuth } from '@/providers/use-app-auth';
import { LEO_SUITE } from '@/config/suite.config';

const SIZE_CLASS = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
} as const;

/** Versão do logo por defeito — incrementar força refresh em browsers/PWA */
const DEFAULT_LOGO = `${LEO_SUITE.defaultAppLogoUrl}?v=lc3`;

/** URLs antigas (Terapia do Toque / uploads legados) — ignorar e usar logo LC */
function isLegacyBrandingUrl(url: string): boolean {
  const lower = url.toLowerCase();
  return (
    lower.includes('terapia') ||
    lower.includes('terapiadotoque') ||
    lower.includes('heloisa') ||
    lower.includes('terapia-do-toque')
  );
}

function resolveLogoUrl(customUrl: string | null | undefined): string {
  const envUrl = import.meta.env.VITE_APP_LOGO_URL as string | undefined;
  if (customUrl?.trim() && !isLegacyBrandingUrl(customUrl)) return customUrl;
  if (envUrl?.trim() && !isLegacyBrandingUrl(envUrl)) return envUrl;
  return DEFAULT_LOGO;
}

interface AppLogoProps {
  size?: keyof typeof SIZE_CLASS;
  className?: string;
}

/** Logo da app admin — personalizável em Definições (Leo Suite) */
export function AppLogo({ size = 'sm', className }: AppLogoProps) {
  const { clinic } = useAppAuth();
  const { data: settings } = useSettings(clinic?.id ?? null);
  const logoUrl = resolveLogoUrl(settings?.app_branding?.logo_url);

  return (
    <img
      src={logoUrl}
      alt={LEO_SUITE.productName}
      className={cn('shrink-0 rounded-lg object-contain bg-transparent', SIZE_CLASS[size], className)}
    />
  );
}

export function useAppLogoUrl(): string {
  const { clinic } = useAppAuth();
  const { data: settings } = useSettings(clinic?.id ?? null);
  return resolveLogoUrl(settings?.app_branding?.logo_url);
}

export function useHasCustomLogo(): boolean {
  const { clinic } = useAppAuth();
  const { data: settings } = useSettings(clinic?.id ?? null);
  const url = settings?.app_branding?.logo_url;
  return !!url?.trim() && !isLegacyBrandingUrl(url);
}
